/**
 * @title Cart of tradeins
 * @description When one or more items has been selected for tradein, the cart will be shown
 * @image https://storageaccountourimbea2.blob.core.windows.net/tradein2guide/return-cart.png
 */
import {atom, selector, useRecoilValue} from "recoil";
import {ConsumerPotentialFragment, EnumTradeInPayoutType} from "../../../Queries";
import React from "react";
import {AppBar, Box, CircularProgress, useMediaQuery} from "@mui/material";
import {makeStyles} from "@mui/styles";
import {AccessContextData} from "../../../providers/access/AppContextProvider";
import {
   CartContinueButton,
   CartPreviousButton,
   GlobalLayout,
   StyledTypography
} from "../../../providers/layout/LayoutProvider";
import {ArrowIcon, ArrowIconBack} from "../../../svg/SvgIcons";
import {LocalizationsAtom} from "../../../providers/localization/LocalizationProvider";
import {Currency} from "../../../components/Currency";
import zIndex from "@mui/material/styles/zIndex";

type CommentDataType = {
   potentialId: string
   comment: string
}

type ReturnsCartData = {
   potentials: ConsumerPotentialFragment[]
   version: number
   payoutType: EnumTradeInPayoutType
   comments: CommentDataType[]
}

export const ReturnsCartAtom = atom<ReturnsCartData>({
   key: 'ReturnsCart',
   default: {
      potentials: [],
      version: 0,
      payoutType: EnumTradeInPayoutType.Voucher,
      comments: []
   }
});

export const ReturnsSumNoPayout = selector<number>({
   key: 'ReturnsCartSumNoPayout',
   get: ({get}) => {
      const data = get(ReturnsCartAtom)
      return data.potentials.reduce((cur, a) => {
         return cur + (a.offer?.value || 0)
      }, 0);
   }
})

export const ReturnsSumPayout = selector<number>({
   key: 'ReturnsCartSumPayout',
   get: ({get}) => {
      const data = get(ReturnsCartAtom)

      const sumBefore = data.potentials.reduce((cur, a) => {
         return cur + (a.offer?.value || 0)
      }, 0);

      const context = get(AccessContextData);
      const payoutPct = context.tradeInPayoutPct;
      return Math.round(sumBefore * payoutPct / 10) / 10
   }
})

export const ReturnsSum = selector<number>({
   key: 'ReturnsCartSum',
   get: ({get}) => {
      const data = get(ReturnsCartAtom)
      if (data.payoutType === EnumTradeInPayoutType.Voucher) {
         return get(ReturnsSumNoPayout);
      } else if (data.payoutType === EnumTradeInPayoutType.Payout) {
         return get(ReturnsSumPayout);
      } else {
         return get(ReturnsSumNoPayout);
      }
   }
})



type ReturnsCartProps = {
   onPrev: () => void
   onNext: () => void
   prevVisible?: boolean
   nextEnabled?: boolean
   swirl?: boolean
}

const staticBar = [
   '6438fef8115629a57d746b9e',
   '6239815f1230216e6f5d4490',
   '623982371230216e6f5d7019',
   '623981c01230216e6f5d494c',
   '63808a12420956df7e2e95ca',
   '6239821ad3ff6e16093c7125',
   '63808949420956df7e2dc81a',
   '638a113e1a69f93e441d838c',
   '64219aae3d85b1c429993cef'
]

const oldStyle = [
   '63d636f2a0217c6a784988ca',
   '640768d51235ada984f6d8b3',
   '65c5de848b8046d303b19974',
   '65f18b0d0588f0de9e36cff0',
   '64ba6c0891aeccd0b316123e',
   '657812f5d4b5b38d9473cd94'
]

export const ReturnsCartView = ({onNext, onPrev, prevVisible, nextEnabled, swirl}: ReturnsCartProps) => {
   const localizations = useRecoilValue(LocalizationsAtom);
   const appContext = useRecoilValue(AccessContextData);
   const sum = useRecoilValue(ReturnsSum);
   const classes = useStyles();
   const globalLayout = useRecoilValue(GlobalLayout);
   const isMobile = useMediaQuery('(max-width:800px)');

   const button = globalLayout.widgets?.continueButton;

   if (sum === 0) {
      return <></>
   }

   const isStatic = staticBar.includes(appContext.shopId);
   const isOldStyle = oldStyle.includes(appContext.shopId);

   const LeftPanel = () => {
      return <Box className={classes.leftPanel}>
         <Box>
            <CartPreviousButton visible={prevVisible || false} onClick={onPrev}>
               <ArrowIconBack style={{width: '16px', height: '14px'}}
                              iconcolor={globalLayout.widgets?.cartTextColor || '#000000'}/>
            </CartPreviousButton>
         </Box>
         <Box>
            {localizations.returnsCartHeaderReplacement &&
               <StyledTypography type={"main"} variant={"body1"} className={classes.leftPanelHeader}
                                 style={{color: globalLayout.widgets?.cartTextColor || undefined}}
               >
                  {localizations.returnsCartHeaderReplacement}
               </StyledTypography>
            }
            {!localizations.returnsCartHeaderReplacement &&
               <StyledTypography type={"main"} variant={"body1"} className={classes.leftPanelHeader}
                                 style={{color: globalLayout.widgets?.cartTextColor || undefined}}
               >
                  {localizations.returnsCartHeader} {appContext.currency !== 'POINT' ? appContext.brandName : ''}
                  {isOldStyle &&
                     <Currency sx={{marginLeft: '8px', fontWeight: 'bold'}} amount={sum}/>
                  }
               </StyledTypography>
            }
         </Box>
      </Box>
   }

   let disabledColor = button?.disabledTextColor || button?.textColor || '#C0C0C0'
   let _buttonColor = button?.textColor || '#000000';
   let buttonColor = nextEnabled ? _buttonColor : disabledColor;

   const RightPanel = () => {
      return <Box sx={{
         display: 'flex',
         flexDirection: 'column',
         justifyContent: 'center',
         alignItems: 'right',
         '@media only screen and (max-width: 800px)': {
            alignItems: 'center'
         }
      }}>
         <Box>
            <CartContinueButton onClick={onNext} nextEnabled={!!nextEnabled}>
               {isOldStyle &&
                  localizations.returnsCartNext
               }
               {!isOldStyle &&
                  <Currency showMainCurrency={true} amount={sum}/>
               }
               <div style={{width: '20px'}}>&nbsp;</div>

               {swirl ?
                  <CircularProgress size={globalLayout?.widgets?.continueButton?.fontSize || '14px'} style={{
                     color: globalLayout?.widgets?.continueButton?.textColor || '#000000'
                  }}/> :
                  <ArrowIcon style={{width: '16px', height: '14px'}}
                             iconcolor={buttonColor}/>
               }
            </CartContinueButton>
         </Box>
      </Box>
   }

   return <Box className={(isStatic || isMobile) ? classes.staticBar : classes.stickyBar}>
      <AppBar className={classes.appBar}
              variant={"outlined"}
              elevation={0}
              style={{
                 backgroundColor: globalLayout.widgets?.cartBackgroundColor || undefined,
              }}
      >
         <LeftPanel/>
         <RightPanel/>
      </AppBar>
   </Box>
}

const useStyles = makeStyles({
   stickyBar: {
      top: 'auto',
      bottom: 0,
      position: 'sticky',
      minWidth: '800px',
      marginTop: '50px',
      zIndex: zIndex.modal - 1
   },
   staticBar: {
      top: 'auto',
      bottom: 0,
      position: 'fixed',
      minWidth: '800px',
      marginTop: '50px',
      zIndex: zIndex.modal - 1
   },
   appBar: {
      backgroundColor: '#F4E4BA',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      height: '68px',
      paddingLeft: '20px',
      paddingRight: '20px',
      paddingTop: '20px',
      paddingBottom: '20px',
      bottom: 0,
      position: 'relative',
      border: '1px solid transparent !important',
      borderRadius: '2px'
   },
   leftPanel: {
      padding: '10px',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      marginRight: '10px'
   },
   leftPanelHeader: {
      marginRight: '20px'
   },
   leftPanelTotal: {
      fontWeight: 'bold',
      whiteSpace: 'nowrap'
   },

   '@media only screen and (max-width: 800px)': {
      stickyBar: {
         position: 'fixed',
         top: 'auto',
         bottom: '0',
         minWidth: 'auto'
      },
      staticBar: {
         position: 'fixed',
         top: 'auto',
         bottom: '0',
         minWidth: 'auto',
         left: 0, right: 0
      },
      leftPanelHeader: {
         marginRight: '0'
      },
      appBar: {
         paddingLeft: '5px',
         paddingRight: '5px',
         flexDirection: 'column',
         height: 'unset'
      },
      leftPanel: {
         padding: '0px'
      }
   },
});
